import { createAsyncThunk } from '@reduxjs/toolkit';
import UserService from '../../../api/services/UserService';
import { base64ToFile } from '../../../utils/util';
import { ALBUM_MEDIA_TYPE, CRM_ORGANIZATION_MEMBER_ROLE, MEDIA_ASSET_SALE_TAG } from '../../../utils/constants';
import axios from 'axios';

export const fetchUserInfo = createAsyncThunk('user/fetchUserInfo', async () => {
  return await UserService.fetchUserInfo();
});

export const changePassword = createAsyncThunk('user/changePassword', async requestData => {
  return await UserService.changePassword(requestData);
});

export const fetchUserEarnings = createAsyncThunk('user/fetchUserEarnings', async requestData => {
  return await UserService.fetchUserEarnings(requestData);
});

export const handleWithdrawalMethods = createAsyncThunk('user/handleWithdrawalMethods', async requestData => {
  const {
    verificationDocumentFront,
    verificationDocumentBack,
    accountNumber,
    routingNumber,
    beneficiaryName,
    beneficiaryAddress,
    ssn,
    profileType,
    swiftCode,
    bankName,
    beneficiaryIBAN,
    address,
    network,
    withdrawalMethodType,
  } = requestData;

  const formData = new FormData();

  if (verificationDocumentFront) {
    formData.append('verificationDocumentFront', verificationDocumentFront.file);
  }
  if (verificationDocumentBack) {
    formData.append('verificationDocumentBack', verificationDocumentBack.file);
  }
  if (accountNumber) {
    formData.append('accountNumber', accountNumber);
  }
  if (routingNumber) {
    formData.append('routingNumber', routingNumber);
  }
  if (beneficiaryName) {
    formData.append('beneficiaryName', beneficiaryName);
  }
  if (beneficiaryAddress) {
    formData.append('beneficiaryAddress', beneficiaryAddress);
  }
  if (ssn) {
    formData.append('ssn', ssn);
  }
  if (profileType) {
    formData.append('profileType', profileType);
  }
  if (swiftCode) {
    formData.append('swiftCode', swiftCode);
  }
  if (bankName) {
    formData.append('bankName', bankName);
  }
  if (beneficiaryIBAN) {
    formData.append('beneficiaryIBAN', beneficiaryIBAN);
  }
  if (address) {
    formData.append('address', address);
  }
  if (network) {
    formData.append('network', network);
  }
  formData.append('withdrawalMethodType', withdrawalMethodType);

  return await UserService.handleWithdrawalMethods(formData);
});

export const updateUserBasicInfo = createAsyncThunk('user/updateUserBasicInfo', async requestData => {
  const { name, dateOfBirth, profilePhoto } = requestData;

  const formData = new FormData();
  if (profilePhoto) {
    const profilePhotoFile = base64ToFile(profilePhoto, 'profile-photo');
    formData.append('file', profilePhotoFile, profilePhotoFile.name);
  }

  formData.append('name', name);
  formData.append('dateOfBirth', dateOfBirth);

  return await UserService.updateBasicInfo(formData);
});

export const fetchInfluencers = createAsyncThunk('user/fetchInfluencers', async requestData => {
  return await UserService.fetchInfluencers(requestData);
});

export const fetchUserMonthlyEarnings = createAsyncThunk('user/fetchUserMonthlyEarnings', async () => {
  return await UserService.fetchUserMonthlyEarnings();
});

export const fetchAlbumMedia = createAsyncThunk('user/fetchAlbumMedia', async requestData => {
  const { kind, tag, page, exactMatch, influencerId } = requestData;
  return await UserService.fetchAlbumMedia(tag, kind, page, null, exactMatch, null, influencerId);
});

export const fetchAssets = createAsyncThunk(
  'user/fetchAssets',
  async requestData => {
    const { kind, tag, page, userInfluencerId, exactMatch, folderId, influencerId } = requestData;
    return await UserService.fetchAlbumMedia(tag, kind, page, userInfluencerId, exactMatch, folderId, influencerId);
  },
  {
    condition: (_, { getState }) => {
      return [
        CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
        CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
        CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
        CRM_ORGANIZATION_MEMBER_ROLE.CHATTER,
        CRM_ORGANIZATION_MEMBER_ROLE.CONTENT_MANAGER,
        CRM_ORGANIZATION_MEMBER_ROLE.REVIEWER,
      ].includes(getState().userInfo.user?.role);
    },
  },
);

export const fetchMediaBundles = createAsyncThunk(
  'user/fetchMediaBundles',
  async requestData => {
    const { userInfluencerId, influencerId } = requestData;
    return await UserService.fetchMediaBundles(userInfluencerId, influencerId);
  },
  {
    condition: (_, { getState }) => {
      return [
        CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
        CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
        CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
        CRM_ORGANIZATION_MEMBER_ROLE.CHATTER,
        CRM_ORGANIZATION_MEMBER_ROLE.CONTENT_MANAGER,
        CRM_ORGANIZATION_MEMBER_ROLE.REVIEWER,
      ].includes(getState().userInfo.user?.role);
    },
  },
);

export const editAlbumMedia = createAsyncThunk('user/editAlbumMedia', async requestData => {
  const { mediaId, description, category, caption, cost, sellWithAi, influencerId } = requestData;
  const body = { aiDescription: description, sellWithAi };
  body.category = category?.value;
  if (cost) {
    body.cost = cost;
  }
  if (caption) {
    body.caption = caption;
  }
  return await UserService.editAlbumMedia(mediaId, body, influencerId);
});

export const uploadMedia = createAsyncThunk('user/uploadMedia', async requestData => {
  const { onUploadProgress, media, kind, tag } = requestData;
  const formData = new FormData();
  formData.append('file', media.file, media.file.name);
  formData.append('caption', media.caption);
  formData.append('aiDescription', media.description);
  formData.append('tag', tag);
  if (tag === MEDIA_ASSET_SALE_TAG.SINGLE_PAID) {
    formData.append('cost', media.cost);
    formData.append('category', media.category.value);
  }

  if (kind === ALBUM_MEDIA_TYPE.PHOTO) {
    return await UserService.uploadPhoto(formData, onUploadProgress);
  } else {
    return await UserService.uploadVideo(formData, onUploadProgress);
  }
});

export const deleteAlbumMedia = createAsyncThunk('user/deleteAlbumMedia', async requestData => {
  const { mediaId, influencerId } = requestData;
  return await UserService.deleteAlbumMedia(mediaId, influencerId);
});

export const uploadBundle = createAsyncThunk('user/uploadBundle', async requestData => {
  const { body, influencerId } = requestData;
  return await UserService.uploadBundleV2(body, influencerId);
});

export const fetchBundleById = createAsyncThunk('user/fetchBundleById', async requestData => {
  const { bundleId, influencerId } = requestData;
  return await UserService.fetchBundleById(bundleId, influencerId);
});

export const deleteBundle = createAsyncThunk('user/deleteBundle', async requestData => {
  const { bundleId, influencerId } = requestData;
  return await UserService.deleteBundle(bundleId, influencerId);
});

export const editBundle = createAsyncThunk('user/editBundle', async requestData => {
  const { bundleId, description, caption, cost, name, influencerId } = requestData;
  return await UserService.editBundle(bundleId, { description, caption, cost, name }, influencerId);
});

export const fetchReferrals = createAsyncThunk('user/fetchReferrals', async () => {
  return await UserService.fetchReferrals();
});

export const uploadW9Form = createAsyncThunk('user/uploadW9Form', async requestData => {
  const { file } = requestData;

  const formData = new FormData();
  formData.append('file', file, file.name);
  return await UserService.uploadW9Form(formData);
});

export const uploadAssistedPhoto = createAsyncThunk('user/uploadAssistedPhoto', async requestData => {
  const { onProgress, photo, type, representativeMedia, influencerId, folderId } = requestData;
  const body = {
    contentType: photo.file.type,
    fileName: photo.file.name,
    tag: type,
  };
  const { url, filename } = await UserService.startUploadAssistedPhoto(body, influencerId);

  await axios.put(url, photo.file, {
    headers: { 'Content-Type': photo.file.type },
    onUploadProgress: progressEvent => {
      const percentCompleted = (progressEvent.loaded / photo.file.size) * 100;
      onProgress(percentCompleted);
    },
  });

  const completeBody = { fileName: filename, tag: type, representativeMedia, ...(folderId && { folderId }) };
  return await UserService.completeUploadAssistedPhoto(completeBody, influencerId);
});

export const uploadStory = createAsyncThunk('user/uploadAssistedPhoto', async requestData => {
  const { onProgress, media, kind, description, influencerId } = requestData;
  const body = {
    contentType: media.file.type,
    fileName: media.file.name,
  };
  const { url, filename } = await UserService.startUploadStory(body, influencerId);

  await axios.put(url, media.file, {
    headers: { 'Content-Type': media.file.type },
    onUploadProgress: progressEvent => {
      const percentCompleted = (progressEvent.loaded / media.file.size) * 100;
      onProgress(percentCompleted);
    },
  });

  const completeBody = { fileName: filename, kind, aiDescription: description };
  return await UserService.completeUploadStory(completeBody, influencerId);
});

export const uploadAssistedVideo = createAsyncThunk('user/uploadAssistedVideo', async requestData => {
  const { video, type, onProgress, representativeMedia, influencerId, folderId } = requestData;
  const body = {
    contentType: video.file.type,
    fileName: video.file.name,
    tag: type,
  };
  const { uploadId, filename } = await UserService.startUploadAssistedVideo(body, influencerId);

  const fileSize = video.file.size;
  const partSize = 15 * 1024 * 1024; // 15MB
  const numParts = Math.ceil(fileSize / partSize);

  let uploadedBytes = 0;

  const etags = [];

  for (let i = 0; i < numParts; i++) {
    const start = i * partSize;
    const end = Math.min(start + partSize, fileSize);
    const blob = video.file.slice(start, end);
    const partNumber = i + 1;

    const uploadedSoFar = uploadedBytes;

    const getUrlBody = {
      partNumber: partNumber,
      fileName: filename,
      uploadId,
    };
    const { url } = await UserService.getUploadAssistedVideoUrl(getUrlBody, influencerId);

    const response = await axios.put(url, blob, {
      headers: { 'Content-Type': video.file.type },
      onUploadProgress: progressEvent => {
        const totalUploaded = uploadedSoFar + progressEvent.loaded;
        const percentCompleted = (totalUploaded / video.file.size) * 100;
        onProgress(percentCompleted);
      },
    });
    const etag = response.headers.etag?.replace(/"/g, '');
    etags.push({ ETag: etag, PartNumber: partNumber });
    uploadedBytes += blob.size;
  }
  const completeBody = {
    fileName: filename,
    uploadId,
    parts: etags,
    tag: type,
    fileSize,
    representativeMedia,
    ...(folderId && { folderId }),
  };
  return await UserService.completeUploadAssistedVideo(completeBody, influencerId);
});

export const testVoice = createAsyncThunk('user/testVoice', async requestData => {
  const { message, influencerId } = requestData;
  return await UserService.testVoice(message, influencerId);
});

export const editManualChatTotalSpentThreshold = createAsyncThunk(
  'user/editManualChatTotalSpentThreshold',
  async requestData => {
    const { manualChatTotalSpentThreshold, influencerId } = requestData;
    return await UserService.editManualChatTotalSpentThreshold(manualChatTotalSpentThreshold, influencerId);
  },
);

export const uploadMediaFromDropbox = createAsyncThunk('user/uploadMediaFromDropbox', async requestData => {
  const { influencerId, dropboxPath, folders } = requestData;
  return await UserService.uploadMediaFromDropbox(influencerId, dropboxPath, folders);
});

export const getDropboxSyncStatus = createAsyncThunk(
  'user/getDropboxSyncStatus',
  async requestData => {
    const { influencerId } = requestData;
    return await UserService.getDropboxSyncStatus(influencerId);
  },
  {
    condition: (_, { getState }) => {
      return [
        CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
        CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
        CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
      ].includes(getState().userInfo.user?.role);
    },
  },
);

export const getOrCreateIdVerificationToken = createAsyncThunk('user/getOrCreateIdVerificationToken', async () => {
  return await UserService.getOrCreateIdVerificationToken();
});
